// Présences d'une occurrence d'événement.
// Règle héritée de Playbook BCL : une personne CONVOQUÉE est présente par
// défaut ; seule une absence déclarée (par elle, un parent ou le coach) la
// sort. Convoquées = membres réguliers de l'équipe + renforts invités sur
// cette date précise. Une réponse d'une personne non convoquée est ignorée.
//
// Indisponibilités (D-009, D-114) : une personne indisponible pour CETTE
// occurrence (période, ou règle de la semaine avec son heure : availability.ts)
// est absente d'office, sans avoir à répondre ; au match, elle peut être
// présente « sans échauffement ». Sa réponse explicite à CETTE séance l'emporte
// (« Je viens quand même », « Incertaine ») : c'est l'exception, la règle reste.
//
// Incertaine (D-116) : comptée à part avant la séance ; après la séance, une
// incertaine jamais confirmée compte comme présente (une convoquée est présente
// par défaut), sauf correction du coach.
import { availabilityFor, type OccInfo, type Period, type Verdict, type WeeklyRule } from './availability';

export interface AttendanceResponse {
  personId: string;
  status: 'present' | 'absent' | 'uncertain';
  reason?: string | null;
}

export type Unavailability = Period;

export interface Absence {
  personId: string;
  reason: string | null;
  medical: boolean;                          // seule information de santé notée (D-009)
  source: 'response' | 'unavailability' | 'rule';
  /** Règle de la semaine : « mercredi soir ». */
  label?: string | null;
}

export interface Attendance {
  /** Attendues (réponse, ou par défaut), « sans échauffement » comprises ; après la séance, les incertaines aussi. */
  present: string[];
  /** Incertaines, avant la fin de la séance (et leur mot pour le coach). */
  uncertain: { personId: string; reason: string | null }[];
  absent: Absence[];
  /** Présentes « sans échauffement » (match : arrivée après le rendez-vous, avant le coup d'envoi). */
  late: Map<string, Verdict>;
  /** Indisponibles qui ont répondu quand même pour cette séance (l'exception) : la raison qui reste. */
  overridden: Map<string, Verdict>;
  /** Incertaines jamais confirmées, comptées présentes après la séance. */
  unconfirmed: string[];
  reinforcements: string[];   // renforts convoqués sur cette date
}

export interface AttendanceContext {
  date: string;               // jour de l'occurrence, 'AAAA-MM-JJ'
  unavailabilities: Unavailability[];
  /** Règles de la semaine (D-114). */
  rules?: WeeklyRule[];
  /** L'occurrence (heure, route, rendez-vous) : sans elle, seules les périodes comptent, à la journée. */
  occ?: OccInfo;
  /** La séance est finie : une incertaine compte comme présente. */
  past?: boolean;
}

/** Indisponibilités qui couvrent ce jour, par personne (médicale si l'une l'est). */
export function unavailableOn(list: Unavailability[], date: string): Map<string, { medical: boolean }> {
  const out = new Map<string, { medical: boolean }>();
  for (const u of list) {
    if (u.startsOn <= date && date <= u.endsOn) {
      out.set(u.personId, { medical: (out.get(u.personId)?.medical ?? false) || u.isMedical });
    }
  }
  return out;
}

/** Ce que la règle dit d'une personne pour cette occurrence (sans tenir compte de sa réponse). */
export function verdictOf(personId: string, ctx: AttendanceContext): Verdict | null {
  if (ctx.occ) return availabilityFor(personId, ctx.occ, ctx.unavailabilities, ctx.rules ?? []);
  const day = unavailableOn(ctx.unavailabilities.filter(u => u.personId === personId), ctx.date).get(personId);
  return day ? { state: 'off', source: 'period', ref: null, medical: day.medical, label: null, freeAt: null, travelMin: 0, meetMin: null } : null;
}

export function resolveAttendance(
  members: string[],
  invited: string[],
  responses: AttendanceResponse[],
  ctx?: AttendanceContext,
): Attendance {
  const convoked: string[] = [];
  const seen = new Set<string>();
  for (const id of [...members, ...invited]) {
    if (!seen.has(id)) { seen.add(id); convoked.push(id); }
  }
  const memberSet = new Set(members);
  const byPerson = new Map(responses.map(r => [r.personId, r]));
  const out: Attendance = { present: [], uncertain: [], absent: [], late: new Map(), overridden: new Map(), unconfirmed: [], reinforcements: [] };
  for (const id of convoked) {
    const r = byPerson.get(id);
    const v = ctx ? verdictOf(id, ctx) : null;
    if (r?.status === 'absent') {
      out.absent.push({ personId: id, reason: r.reason ?? null, medical: v?.source === 'period' ? v.medical : false, source: 'response' });
      continue;
    }
    if (r && v?.state === 'off') out.overridden.set(id, v);
    if (r?.status === 'uncertain') {
      if (ctx?.past) { out.present.push(id); out.unconfirmed.push(id); }
      else out.uncertain.push({ personId: id, reason: r.reason ?? null });
    } else if (!r && v?.state === 'off') {
      out.absent.push(v.source === 'rule'
        ? { personId: id, reason: null, medical: false, source: 'rule', label: v.label }
        : { personId: id, reason: null, medical: v.medical, source: 'unavailability' });
    } else {
      out.present.push(id);
      if (v?.state === 'late') out.late.set(id, v);
    }
  }
  out.reinforcements = convoked.filter(id => !memberSet.has(id));
  return out;
}

/** Toutes celles qui peuvent jouer : les attendues et les incertaines (ordre de la convocation). */
export const expectedOf = (a: Attendance): string[] => [...a.present, ...a.uncertain.map(u => u.personId)];

/** Les chiffres d'une occurrence, les mêmes partout (planning, matchs de l'équipe, fiche : D-103, un seul état) :
 *  présentes (par défaut, sans réponse), incertaines, absentes, sur les convoquées. */
export function attendanceCounts(a: Attendance): { present: number; uncertain: number; absent: number; total: number } {
  return { present: a.present.length, uncertain: a.uncertain.length, absent: a.absent.length, total: a.present.length + a.uncertain.length + a.absent.length };
}

/** Part des présentes, en pourcentage entier (jauge de la fiche de séance). */
export function presenceRate(a: Attendance): number {
  const total = a.present.length + a.uncertain.length + a.absent.length;
  return total ? Math.round((100 * a.present.length) / total) : 0;
}
